/**
 * skee-ball.js — Skee-Ball (GDD 6.4.2).
 *
 * Flat lane → jump ramp → target apron with four concentric ring pockets worth
 * 10, 50, 100 and 500. Sixty seconds, unlimited balls: the dispenser keeps three
 * within reach and scored or dead balls are despawned, so the round has a rhythm
 * rather than an ammunition count.
 *
 * "Must be rolled, not thrown" is the rule that makes this stall itself, so it
 * is enforced at the moment of release rather than left to the physics:
 *
 *   - released more than `maxReleaseHeight` above the lane deck, or
 *   - released pitched further than `maxReleasePitchDeg` above the horizontal
 *
 * …and the ball is a foul. It cannot score, it gets a gentle bounce-back so it
 * visibly dribbles away rather than vanishing, and the ROLL IT! sign lights up.
 * A legal roll runs the length of the deck and up the ramp under its own
 * momentum, which is what lane friction 0.3 is tuned for.
 *
 * Every tuning number lives in constants.js SKEE_BALL.
 *
 * @module stalls/skee-ball
 */

import * as THREE from 'three';
import { SKEE_BALL, HUB, EVENTS } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import { createStallLight } from '../world/lighting.js';
import { grab } from '../core/grab.js';
import { bus } from '../core/events.js';
import { audio } from '../audio/index.js';
import { haptics } from '../core/haptics.js';

const CFG = SKEE_BALL;
const BALL_R = 0.052;
const AXIS_X = new THREE.Vector3(1, 0, 0);
/** Half-thickness of the lane and apron slabs. */
const DECK_HALF = 0.05;

class SkeeBall extends StallBase {
  constructor(ctx) {
    super(ctx);
    /** @type {Array<{handle: object, age: number, armed: boolean, dwell: number, dead: boolean}>} */
    this._balls = [];
    /** Body id → ball record, for resolving sensor events. */
    this._byBody = new Map();
    /** @type {Array<{def: object, handle: object}>} */ this._pockets = [];
    /** @type {Array<object|null>} Dispenser slots. */ this._slots = [];
    this._ballSeq = 0;
    this._rolls = 0;
    this._fouls = 0;
    this._best = 0;

    /** @type {THREE.Mesh|null} */ this.foulSign = null;
    this._foulT = 0;
    /** @type {THREE.Mesh|null} */ this.returnBall = null;
    this._returnT = -1;
    this._offCollision = null;

    this._plotQuatInv = new THREE.Quaternion();
    this._v = new THREE.Vector3();
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    this._plotQuatInv.copy(this.worldQuaternion()).invert();

    this._buildLane();
    this._buildApron();
    this._buildPockets();
    this._buildFurniture();

    // Sensor entry only ARMS a ball; where it settles decides the ring.
    this._offCollision = bus.on(EVENTS.COLLISION, (e) => this._onCollision(e));
  }

  /** @private */
  _buildLane() {
    const laneLen = CFG.laneZEnd - CFG.laneZStart;
    this._slab(CFG.laneWidth, DECK_HALF * 2, laneLen, {
      x: 0, y: CFG.laneY - DECK_HALF, z: (CFG.laneZStart + CFG.laneZEnd) / 2,
    }, 0x8d6a45, CFG.laneFriction);

    // The jump. Its top surface runs from the lane deck to the ramp lip, so the
    // slab is dropped by its own half-thickness along the incline.
    const dz = CFG.rampZEnd - CFG.laneZEnd;
    const dy = CFG.rampTopY - CFG.laneY;
    const angle = Math.atan2(dy, dz);
    const len = Math.hypot(dz, dy);
    const ramp = new THREE.Mesh(
      new THREE.BoxGeometry(CFG.laneWidth, DECK_HALF * 2, len),
      factory.mat(0x9b7a52, { rough: 0.8 }),
    );
    ramp.receiveShadow = true;
    ramp.rotation.x = -angle;
    // Offset the slab centre along its own tilted normal, so the TOP surface —
    // the one the ball rides — runs exactly from the lane deck to the ramp lip.
    // Getting this wrong leaves a step at the junction for the ball to catch on.
    const rampPos = {
      x: 0,
      y: (CFG.laneY + CFG.rampTopY) / 2 - DECK_HALF * Math.cos(angle),
      z: (CFG.laneZEnd + CFG.rampZEnd) / 2 + DECK_HALF * Math.sin(angle),
    };
    this.spawn(ramp, rampPos, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: CFG.laneWidth / 2, y: DECK_HALF, z: len / 2 },
      position: this.localToWorld(rampPos.x, rampPos.y, rampPos.z),
      rotation: this.worldQuaternion().multiply(
        new THREE.Quaternion().setFromAxisAngle(AXIS_X, -angle),
      ),
      friction: CFG.laneFriction, restitution: 0.25,
    });

    // Side rails keep a roll straight instead of letting it wander off the deck.
    for (const side of [-1, 1]) {
      this._slab(0.05, 0.2, laneLen + dz, {
        x: side * (CFG.laneWidth / 2 + 0.025),
        y: CFG.laneY + 0.05,
        z: (CFG.laneZStart + CFG.rampZEnd) / 2,
      }, 0x6d5230, 0.4);
    }
  }

  /** @private */
  _buildApron() {
    const deckLen = CFG.deckZEnd - CFG.deckZStart;
    // Light enough that the coloured ring markers still read against it.
    this._slab(CFG.deckWidth, DECK_HALF * 2, deckLen, {
      x: 0, y: CFG.deckY - DECK_HALF, z: (CFG.deckZStart + CFG.deckZEnd) / 2,
    }, 0x5b6c94, 0.45);

    for (const side of [-1, 1]) {
      this._slab(0.06, 0.34, deckLen, {
        x: side * (CFG.deckWidth / 2 + 0.03), y: CFG.deckY + 0.17,
        z: (CFG.deckZStart + CFG.deckZEnd) / 2,
      }, 0x8d7aa8, 0.6);
    }
    this._slab(CFG.deckWidth + 0.12, 0.7, 0.08, {
      x: 0, y: CFG.deckY + 0.35, z: CFG.deckZEnd + 0.04,
    }, 0x4a3a5c, 0.6);
    // A low front lip so a ball that lands short cannot roll back down the ramp.
    this._slab(CFG.deckWidth, 0.1, 0.06, {
      x: 0, y: CFG.deckY + 0.05, z: CFG.deckZStart - 0.03,
    }, 0x4a3a5c, 0.6);
  }

  /**
   * The concentric pockets. Nested sensors: entering any of them arms the ball,
   * and the innermost one that still contains it when it settles is the score.
   * @private
   */
  _buildPockets() {
    this._pockets = [];
    CFG.rings.forEach((def, i) => {
      // Terraced rather than painted flat. The apron is 4.5 m out and only
      // 0.46 m below eye level, so flat discs are seen at about 6° and read as
      // a single sliver; stepping each ring up as it narrows makes the target
      // legible from the counter. The steps are decor — scoring is by radius,
      // and the deck underneath is flat.
      const step = 0.012 + (CFG.rings.length - 1 - i) * 0.013;
      const marker = new THREE.Mesh(
        new THREE.CylinderGeometry(def.radius, def.radius, step, 28),
        factory.mat(def.color, { rough: 0.5 }),
      );
      marker.position.set(0, CFG.deckY + step / 2, CFG.ringCentreZ);
      marker.receiveShadow = true;
      this.root.add(marker);

      const label = factory.createSign('', {
        w: 0.3, h: 0.14, lines: [`${def.points}`], bg: '#12172a',
      });
      label.position.set(
        def.radius - 0.14, CFG.deckY + step + 0.004, CFG.ringCentreZ - def.radius + 0.08,
      );
      label.rotation.x = -Math.PI / 2;
      this.root.add(label);

      const sensor = new THREE.Object3D();
      const handle = this.spawn(sensor, { x: 0, y: CFG.deckY + 0.06, z: CFG.ringCentreZ }, {
        type: 'fixed', sensor: true, shape: 'cylinder',
        radius: def.radius, halfHeight: 0.06,
        position: this.localToWorld(0, CFG.deckY + 0.06, CFG.ringCentreZ),
      });
      handle.userData.pocket = def.key;
      this._pockets.push({ def, handle });
    });
  }

  /** @private */
  _buildFurniture() {
    const width = HUB.boothWidth - 0.4;
    this._slab(width, 0.1, 6.2, { x: 0, y: -0.05, z: 2.9 }, 0x6a5a44, 0.8);
    this._slab(width, 2.4, 0.1, { x: 0, y: 1.2, z: 5.8 }, 0x6d5f7a, 0.9);

    // The ball return, alongside the lane at hand height.
    this._slab(CFG.rack.width, 0.06, 0.26, {
      x: CFG.rack.x, y: CFG.rack.y, z: CFG.rack.z,
    }, 0x6d5230, 0.7);
    this._slab(CFG.rack.width, 0.09, 0.04, {
      x: CFG.rack.x, y: CFG.rack.y + 0.05, z: CFG.rack.z - 0.15,
    }, 0x8d5524, 0.7);

    this.buildScoreboard({ y: 2.55, z: 3.0, width: 1.6, height: 0.95 });

    this.foulSign = factory.createSign('', {
      w: 0.92, h: 0.3, lines: ['ROLL IT!'], bg: '#5c1f22', color: '#ffd166',
    });
    this.foulSign.position.set(0, CFG.rampTopY + 0.5, CFG.rampZEnd + 0.1);
    this.foulSign.rotation.y = Math.PI;
    this.foulSign.visible = false;
    this.root.add(this.foulSign);

    // Ball return: a chute down the right-hand side and one ball riding it.
    const chute = new THREE.Mesh(
      new THREE.BoxGeometry(0.14, 0.05, CFG.deckZStart - 0.4),
      factory.mat(0x6d5230, { rough: 0.8 }),
    );
    chute.position.set(0.46, CFG.laneY - 0.16, (CFG.deckZStart + 0.4) / 2);
    chute.rotation.x = 0.06;
    this.root.add(chute);

    this.returnBall = factory.createBall('skee');
    this.returnBall.visible = false;
    this.root.add(this.returnBall);

    const light = createStallLight(0xffe0b0, 3.6, 14);
    light.position.set(0, 2.6, 2.4);
    this.root.add(light);
  }

  /** A fixed box of booth structure, authored in local space. @private */
  _slab(w, h, d, pos, color, friction) {
    const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), factory.mat(color, { rough: 0.85 }));
    box.receiveShadow = true;
    this.spawn(box, pos, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: w / 2, y: h / 2, z: d / 2 },
      position: this.localToWorld(pos.x, pos.y, pos.z),
      friction: friction ?? 0.6, restitution: 0.2,
    });
    return box;
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({ seconds: CFG.roundSeconds });
    this._plotQuatInv.copy(this.worldQuaternion()).invert();
    this._balls = [];
    this._byBody.clear();
    this._slots = new Array(CFG.maxLiveBalls).fill(null);
    this._ballSeq = 0;
    this._rolls = 0;
    this._fouls = 0;
    this._best = 0;
    this._foulT = 0;
    this._returnT = -1;
    if (this.foulSign) this.foulSign.visible = false;
    if (this.returnBall) this.returnBall.visible = false;

    // The pocket bodies are static and built once, but `_beginRound` clears the
    // target list — so the pockets are re-declared here, per round, or a test
    // would find them missing from `__cc.stalls.targets()`.
    for (const p of this._pockets) {
      this.registerTarget(p.handle, { id: `pocket-${p.def.key}`, group: 'pocket' });
    }

    this._refill();
  }

  /** @private */
  _slotX(slot) {
    return CFG.rack.x + (slot - (CFG.maxLiveBalls - 1) / 2) * CFG.rack.spacing;
  }

  /** Top up the dispenser. This is what "unlimited balls" means. @private */
  _refill() {
    if (!this.roundActive) return;
    for (let s = 0; s < CFG.maxLiveBalls; s++) {
      const held = this._slots[s];
      if (held && !held.removed && !held.userData.thrown) continue;
      this._slots[s] = null;
      this._spawnBall(s);
    }
  }

  /** @private */
  _spawnBall(slot) {
    const mesh = factory.createBall('skee');
    const handle = this.spawnThrowable(mesh, {
      x: this._slotX(slot), y: CFG.rack.y + 0.03 + BALL_R, z: CFG.rack.z,
    }, {
      friction: CFG.laneFriction, restitution: 0.3, ccd: true, angularDamping: 0.12,
    });
    handle.userData.slot = slot;
    handle.userData.foul = false;

    const record = { handle, age: 0, armed: false, dwell: 0, dead: false, targetId: '' };
    record.targetId = this.registerTarget(handle, { id: `ball-${this._ballSeq++}`, group: 'ball' });
    this._slots[slot] = handle;
    this._balls.push(record);
    this._byBody.set(handle.id, record);
    return handle;
  }

  /** Internals for `__cc.stalls.debug()`. @returns {object} */
  debugState() {
    const live = this._balls.filter((r) => !r.dead && !r.handle.removed);
    return {
      rolls: this._rolls,
      fouls: this._fouls,
      best: this._best,
      inTray: this._slots.filter((h) => h && !h.removed && !h.userData.thrown).length,
      balls: live.map((r) => ({
        targetId: r.targetId,
        thrown: !!r.handle.userData.thrown,
        foul: !!r.handle.userData.foul,
        armed: r.armed,
        local: this.worldToLocal(r.handle.position()).toArray(),
        speed: r.handle.velocity().length(),
      })),
    };
  }

  // ── Release judging ─────────────────────────────────────────────────────

  /** @private */
  _onThrowableReleased(handle) {
    const slot = handle.userData.slot;
    if (slot !== undefined && this._slots[slot] === handle) this._slots[slot] = null;
    this._rolls += 1;
    this._judgeRelease(handle);
    super._onThrowableReleased(handle);
    this.updateScoreboard();
  }

  /**
   * GDD 6.4.2: the ball must be rolled, not thrown. Grab sets the release
   * velocity before calling us, so both the pose and the throw are readable
   * here — which is the only moment they are both true.
   * @private
   */
  _judgeRelease(handle) {
    const local = this.worldToLocal(handle.position());
    const vel = handle.velocity().applyQuaternion(this._plotQuatInv);
    const speed = vel.length();
    const height = local.y - this._laneSurfaceY(local.z);
    const pitch = speed > 1e-4
      ? Math.asin(THREE.MathUtils.clamp(vel.y / speed, -1, 1))
      : 0;

    if (height <= CFG.maxReleaseHeight
      && pitch <= THREE.MathUtils.degToRad(CFG.maxReleasePitchDeg)) return;

    handle.userData.foul = true;
    this._fouls += 1;
    this._foulT = CFG.foulSignSeconds;

    // A gentle bounce-back reads as "that didn't count" far better than
    // freezing the ball mid-air would.
    vel.multiplyScalar(-CFG.foulBounceBack);
    vel.y = Math.min(vel.y, 0.5);
    handle.setVelocity(this.localToWorldDirection(vel));

    audio.play('miss');
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'miss' });
    haptics.play('right', 'miss');
  }

  /** Surface height of the lane at a stall-local Z. @private */
  _laneSurfaceY(z) {
    if (z <= CFG.laneZEnd) return CFG.laneY;
    if (z >= CFG.rampZEnd) return CFG.deckY;
    const k = (z - CFG.laneZEnd) / (CFG.rampZEnd - CFG.laneZEnd);
    return CFG.laneY + k * (CFG.rampTopY - CFG.laneY);
  }

  // ── Scoring ─────────────────────────────────────────────────────────────

  /** @private */
  _onCollision({ a, b, started }) {
    if (!started) return;
    const record = (a && this._byBody.get(a.id)) || (b && this._byBody.get(b.id));
    const pocket = a?.userData?.pocket || b?.userData?.pocket;
    if (record && pocket) record.armed = true;
  }

  /** @private */
  _updateBalls(dt) {
    for (const rec of this._balls) {
      const h = rec.handle;
      if (rec.dead) continue;
      if (h.removed) { rec.dead = true; continue; }
      if (!h.userData.thrown) continue;

      rec.age += dt;
      const local = this.worldToLocal(h.position());
      if (local.y < -0.6 || rec.age > CFG.ballLifetime) { this._retire(rec, false); continue; }
      if (!rec.armed) continue;

      rec.dwell += dt;
      if (h.velocity().length() > CFG.settleSpeed && rec.dwell < CFG.pocketDwell) continue;
      this._scoreBall(rec, local);
    }

    if (this._balls.some((r) => r.dead)) {
      this._balls = this._balls.filter((r) => !r.dead);
    }
    this._refill();
  }

  /** @private */
  _scoreBall(rec, local) {
    if (rec.handle.userData.foul) { this._retire(rec, true); return; }

    const radius = Math.hypot(local.x, local.z - CFG.ringCentreZ);
    const pocket = this._pockets.find((p) => radius <= p.def.radius)
      || this._pockets[this._pockets.length - 1];

    pocket.handle.userData.scored = true;
    rec.handle.userData.scored = true;
    this._best = Math.max(this._best, pocket.def.points);

    this.addScore(pocket.def.points, {
      position: rec.handle.position(),
      label: pocket.def.key === 'centre' ? 'JACKPOT!' : `${pocket.def.key} ring`,
      color: pocket.def.key === 'centre' ? '#d4af37' : '#6fe7c4',
    });
    audio.play(pocket.def.key === 'centre' ? 'prize-earned' : 'impact-hit');
    haptics.play('right', 'score');

    this._retire(rec, true);
  }

  /** Take a ball out of play and send one back down the chute. @private */
  _retire(rec, viaReturn) {
    rec.dead = true;
    grab.unregister(rec.handle);
    const i = this.throwables.indexOf(rec.handle);
    if (i >= 0) this.throwables.splice(i, 1);
    this._byBody.delete(rec.handle.id);
    rec.handle.remove();
    if (viaReturn) this._returnT = 0;
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    this._updateBalls(dt);

    if (this._foulT > 0) {
      this._foulT -= dt;
      if (this.foulSign) this.foulSign.visible = this._foulT > 0;
    }

    if (this._returnT >= 0 && this.returnBall) {
      this._returnT += dt;
      const k = this._returnT / CFG.returnTime;
      if (k >= 1) {
        this.returnBall.visible = false;
        this._returnT = -1;
      } else {
        this.returnBall.visible = true;
        this.returnBall.position.set(
          0.46,
          CFG.laneY - 0.1 - Math.sin(k * Math.PI) * 0.04,
          CFG.deckZStart - k * (CFG.deckZStart - 0.4),
        );
      }
    }
  }

  /** Nudge a roll toward the middle of the rings (GDD 5.4: 8° assist). */
  findAimTarget() {
    return { position: this.localToWorld(0, CFG.deckY + 0.05, CFG.ringCentreZ) };
  }

  updateScoreboard() {
    if (!this.scoreboard) return;
    this.scoreboard.setLines([
      `Score  ${this.score}`,
      `Time   ${Math.ceil(Math.max(0, this.timeLeft))}s`,
      `Rolls  ${this._rolls}   Fouls ${this._fouls}`,
      `Best   ${this._best}`,
    ]);
  }

  onTeardown() {
    if (this._offCollision) { this._offCollision(); this._offCollision = null; }
    this._balls = [];
    this._byBody.clear();
    this._pockets = [];
    this._slots = [];
    this.foulSign = null;
    this.returnBall = null;
    this._returnT = -1;
    this._foulT = 0;
  }
}

registerStall({
  id: 'skee-ball',
  name: 'Skee-Ball',
  cost: CFG.cost,
  create: (ctx) => new SkeeBall(ctx),
});

export { SkeeBall };
